import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { buildPraticaOggetto } from "../lib/oggetto";
import RichTextEditor from "./RichTextEditor";
import RubricaDialog from "./RubricaDialog";
import AllegaDaPraticaDialog from "./AllegaDaPraticaDialog";
import SuggeritiDestinatariDialog from "./SuggeritiDestinatariDialog";
import TestiRapidiPicker from "./TestiRapidiPicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Mail, Loader2, Send, Paperclip, X, AlertTriangle, BookUser, FolderInput } from "lucide-react";
import { toast } from "sonner";

const readFileB64 = (file) =>
  new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve({ nome: file.name, mime: file.type || "application/octet-stream", content_b64: String(r.result).split(",")[1] });
    r.readAsDataURL(file);
  });

export default function EmailDialog({ open, onOpenChange, pratica, initialAttachments, prefillTo, prefillSubject }) {
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [files, setFiles] = useState([]);
  const [praticaAtts, setPraticaAtts] = useState([]); // [{id, nome, content_type}] allegati esistenti
  const [allegaOpen, setAllegaOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [rubricaTarget, setRubricaTarget] = useState(null);
  const [suggOpen, setSuggOpen] = useState(false);
  const editorRef = useRef(null);
  const insertQuickText = (t) => {
    const html = String(t || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>");
    if (editorRef.current) editorRef.current.chain().focus().insertContent(html).run();
    else setHtml((h) => (h || "") + html);
  };

  const canSend = useMemo(() => status?.configured && status?.enabled, [status]);
  const parse = (s) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

  useEffect(() => {
    if (!open) return;
    setTo(prefillTo || ""); setCc(""); setBcc(""); setSubject(prefillSubject || buildPraticaOggetto(pratica)); setHtml("");
    setFiles(initialAttachments && initialAttachments.length ? initialAttachments : []); setPraticaAtts([]);
    api.get("/studio/email/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ configured: false, enabled: false }));
    if (pratica?.id && !prefillTo) setSuggOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addFromSuggeriti = (chosen) => {
    const addrs = chosen.map((c) => c.address);
    const existing = parse(to);
    setTo(Array.from(new Set([...existing, ...addrs])).join(", "));
  };

  const onFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    const encoded = await Promise.all(list.map(readFileB64));
    setFiles((f) => [...f, ...encoded]);
    e.target.value = "";
  };

  const send = async () => {
    const recipients = parse(to);
    if (!recipients.length) { toast.error("Inserisci almeno un destinatario"); return; }
    if (!subject.trim()) { toast.error("Inserisci l'oggetto"); return; }
    if (!html || html.replace(/<[^>]+>/g, "").trim() === "") { toast.error("Inserisci il testo del messaggio"); return; }
    setSending(true);
    try {
      await api.post("/studio/email/send", {
        pratica_id: pratica?.id, to: recipients, cc: parse(cc), bcc: parse(bcc), subject: subject.trim(), html, attachments: files, attachment_ids: praticaAtts.map((a) => a.id),
      });
      toast.success("Email inviata correttamente");
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const rubricaSetters = { to: [to, setTo], cc: [cc, setCc], bcc: [bcc, setBcc] };
  const addFromRubrica = (addresses) => {
    if (!rubricaTarget) return;
    const [cur, setter] = rubricaSetters[rubricaTarget];
    const existing = parse(cur);
    setter(Array.from(new Set([...existing, ...addresses])).join(", "));
  };
  const RUBRICA_LABELS = { to: "Destinatari", cc: "CC", bcc: "CCN" };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <SuggeritiDestinatariDialog open={suggOpen} onClose={() => setSuggOpen(false)} praticaId={pratica?.id} mode="email" onConfirm={addFromSuggeriti} />
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="email-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <Mail className="h-5 w-5 text-amber-400" /> Email{pratica?.numero_pratica ? ` · ${pratica.numero_pratica}` : ""}
          </DialogTitle>
        </DialogHeader>

        {!canSend && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300 flex items-start gap-2" data-testid="email-not-configured">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Email non configurata o disattivata. Il Titolare deve impostarla in <strong>Impostazioni Studio → Configurazione Email</strong> prima di inviare.</span>
          </div>
        )}

        <div className="space-y-4 pt-2">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Destinatari <span className="text-slate-500 font-normal">(separa con virgola)</span></Label>
              <button type="button" onClick={() => setRubricaTarget("to")} data-testid="email-rubrica-to" className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"><BookUser className="h-3.5 w-3.5" /> Rubrica</button>
            </div>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="destinatario@email.it" data-testid="email-to" className="mt-1.5 bg-slate-900/60 border-slate-700" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">CC</Label>
                <button type="button" onClick={() => setRubricaTarget("cc")} data-testid="email-rubrica-cc" className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"><BookUser className="h-3.5 w-3.5" /> Rubrica</button>
              </div>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} data-testid="email-cc" className="mt-1.5 bg-slate-900/60 border-slate-700" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">CCN</Label>
                <button type="button" onClick={() => setRubricaTarget("bcc")} data-testid="email-rubrica-bcc" className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"><BookUser className="h-3.5 w-3.5" /> Rubrica</button>
              </div>
              <Input value={bcc} onChange={(e) => setBcc(e.target.value)} data-testid="email-bcc" className="mt-1.5 bg-slate-900/60 border-slate-700" />
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Oggetto</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" className="mt-1.5 bg-slate-900/60 border-slate-700" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-slate-300">Testo del messaggio</Label>
              <TestiRapidiPicker onInsert={insertQuickText} testId="email-testi-rapidi" />
            </div>
            <RichTextEditor value={html} onChange={setHtml} onEditorReady={(ed) => { editorRef.current = ed; }} />
          </div>

          <div>
            <Label className="text-slate-300 mb-1.5 block">Allegati</Label>
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-2 text-sm text-slate-300 cursor-pointer rounded-md border border-slate-700 px-3 py-2 hover:bg-slate-800/50 w-fit" data-testid="email-attach-label">
                <Paperclip className="h-4 w-4" /> Aggiungi allegati
                <input type="file" multiple className="hidden" onChange={onFiles} data-testid="email-attach-input" />
              </label>
              <button type="button" onClick={() => setAllegaOpen(true)} data-testid="email-allega-da-pratica"
                className="inline-flex items-center gap-2 text-sm text-amber-300 rounded-md border border-amber-500/40 px-3 py-2 hover:bg-amber-500/10 w-fit">
                <FolderInput className="h-4 w-4" /> Allega dalla pratica
              </button>
            </div>
            {(files.length > 0 || praticaAtts.length > 0) && (
              <div className="mt-2 space-y-1">
                {praticaAtts.map((f) => (
                  <div key={f.id} className="flex items-center justify-between gap-2 text-xs text-slate-300 bg-slate-900/50 rounded px-2 py-1" data-testid={`email-pratica-att-${f.id}`}>
                    <span className="truncate flex items-center gap-1.5"><span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 shrink-0">pratica</span>{f.nome}</span>
                    <button type="button" onClick={() => setPraticaAtts((arr) => arr.filter((x) => x.id !== f.id))} className="text-slate-500 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-xs text-slate-300 bg-slate-900/50 rounded px-2 py-1" data-testid={`email-attach-${i}`}>
                    <span className="truncate">{f.nome}</span>
                    <button type="button" onClick={() => setFiles((arr) => arr.filter((_, idx) => idx !== i))} className="text-slate-500 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button onClick={send} disabled={sending || !canSend} data-testid="email-send-button" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Invia Email
            </Button>
          </div>
        </div>
      </DialogContent>
      <RubricaDialog open={!!rubricaTarget} onOpenChange={(o) => !o && setRubricaTarget(null)} onConfirm={addFromRubrica} targetLabel={rubricaTarget ? RUBRICA_LABELS[rubricaTarget] : ""} />
      <AllegaDaPraticaDialog open={allegaOpen} onOpenChange={setAllegaOpen} praticaId={pratica?.id}
        principaleNome={pratica?.nome_assistito || pratica?.assistito_scheda?.ragione_sociale || ""}
        alreadySelected={praticaAtts.map((a) => a.id)}
        onConfirm={(chosen, letterAtts) => {
          setPraticaAtts(chosen.map((c) => ({ id: c.id, nome: c.nome, content_type: c.content_type })));
          if (letterAtts && letterAtts.length) setFiles((f) => { const names = new Set(f.map((x) => x.nome)); return [...f, ...letterAtts.filter((a) => !names.has(a.nome))]; });
        }} />
    </Dialog>
  );
}
